import type { ComponentType, ReactNode } from 'react';

import { cn } from '@/lib/utils';

/** One labelled figure within a {@link StatBar}. */
export interface StatBarItem {
  /** Muted caption above the value. */
  label: string;
  /** The headline figure (string, number, or badge node). */
  value: ReactNode;
  /** Lucide icon rendered in a tinted tile ahead of the label/value. */
  icon?: ComponentType<{ className?: string }>;
  /** Custom leading visual (e.g. a thumbnail) replacing the icon tile. */
  leading?: ReactNode;
}

/** Maps a cell count to its `sm`-and-up column class (kept literal for Tailwind's scanner). */
const COLUMN_CLASS: Record<number, string> = {
  1: 'sm:grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-4',
};

/** Renders a single stat cell — a leading visual plus label/value stack. */
function StatCell({ label, value, icon: Icon, leading }: StatBarItem) {
  return (
    <div className="flex items-center gap-3 px-5 py-4">
      {leading ??
        (Icon ? (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        ) : null)}
      <div className="min-w-0">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="truncate text-lg font-semibold">{value}</p>
      </div>
    </div>
  );
}

/**
 * Horizontal band of labelled stats separated by cell dividers — the
 * canonical "stat trio" shared across the library overview tabs
 * (challenge, artifact, collectible, template). Cells stack vertically on
 * mobile and divide horizontally from the `sm` breakpoint up, with the
 * column count derived from the number of items.
 *
 * @param items - The stats to render (1–4 cells).
 * @param className - Optional extra classes on the outer band.
 */
export function StatBar({ items, className }: { items: StatBarItem[]; className?: string }) {
  if (items.length === 0) return null;
  const columns = COLUMN_CLASS[items.length] ?? COLUMN_CLASS[4];

  return (
    <div className={cn('rounded-xl border bg-card shadow-sm', className)}>
      <div className={cn('grid grid-cols-1 divide-y sm:divide-x sm:divide-y-0', columns)}>
        {items.map((item) => (
          <StatCell key={item.label} {...item} />
        ))}
      </div>
    </div>
  );
}
